import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { ArrowRight, Users, Target, Brain, Heart, Shield, TrendingUp, CheckCircle, Layers, BarChart3, FileText } from 'lucide-react';

const impactASPlusLogo = "/opt/brand/impactas-final-2.webp";
const discAfricaLogo = "/opt/photo/disc-lens-africa.webp";
const discAfricaTeamsLogo = "/opt/photo/disc-lens-teams.webp";

const threeLenses = [
  {
    icon: Target,
    title: "Behaviour",
    subtitle: "How you act",
    description: "Captures observable behavioural patterns using the DISC framework, including pace, focus, interaction style, and responses under pressure."
  },
  {
    icon: Heart,
    title: "Values",
    subtitle: "What you value",
    description: "Reveals the principles that guide decisions, priorities, and standards, providing insight into judgment, leadership legitimacy, and alignment with organisational culture."
  },
  {
    icon: Brain,
    title: "Motivation",
    subtitle: "What sustains you",
    description: "Identifies deep motivational drivers that influence energy, engagement, and long-term sustainability in leadership roles."
  }
];

const integratedStyleHighlights = [
  "Primary and integrated styles",
  "Degree of behavioural adaptation",
  "An Authenticity Score, indicating whether current behaviour is aligned with values and motivation or maintained at personal cost"
];

const valuesInsight = [
  "Why leaders with similar behaviours make different decisions",
  "What leaders will prioritise or compromise under pressure",
  "Where personal values align or clash with organisational culture"
];

const reportIncludes = [
  "Context-specific strengths and risks",
  "Communication and relationship strategies by style",
  "Guidance for working across generations",
  "Conflict and trust-building insight",
  "A structured 90-day growth roadmap"
];

const teamsInsight = [
  "Collective behavioural patterns",
  "Decision-making and influence dynamics",
  "Sources of friction or misalignment",
  "How leadership behaviour shapes execution"
];

const organisationalInsight = [
  "Dominant behavioural patterns",
  "Leadership culture signals",
  "Values alignment and risk areas",
  "Behavioural factors affecting execution"
];

const DiscAfricaPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="DISC Assessment Nigeria | DISC Lens Africa Behavioural Diagnostic Lagos"
        description="DISC Lens Africa™ is Nigeria's most comprehensive behavioural assessment combining DISC styles, values, and motivation. Purpose-built for African leadership contexts. Used by leading Nigerian banks, corporates, and multinationals for leadership development."
        keywords="DISC assessment Nigeria, DISC test Lagos, behavioural assessment Nigeria, personality test Lagos, DISC profile Nigeria, DISC certification Africa, behavioural profiling Nigeria, leadership style assessment Lagos, DISC workshop Nigeria"
        canonical="/disc-africa"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section 
          className="relative min-h-[70vh] lg:min-h-[75vh] flex items-center bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(10, 10, 10, 0.95) 0%, rgba(10, 10, 10, 0.8) 40%, rgba(10, 10, 10, 0.4) 100%), url('/opt/photo/ext/u1655720357872-ce227e4164ba.webp')`
          }}
        >
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8 pt-24">
            <div className="max-w-3xl">
              <h1 className="text-white font-semibold text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight mb-6">
                DISC Lens Africa™
              </h1>
              
              <p className="text-[#2693d0] text-lg lg:text-xl xl:text-2xl font-medium leading-relaxed mb-8">
                Multi-lens behavioural, values, and motivation insight for African organisations
              </p>
            </div>
            
            {/* Logo badge - top right */}
            <div className="absolute top-24 right-6 lg:right-8 xl:right-16 hidden md:block">
              <img 
                src={discAfricaLogo} 
                alt="DISC Lens Africa" 
                className="h-16 lg:h-20 xl:h-24 w-auto opacity-90"
              />
            </div>
          </div>
        </section>

        {/* Section 1: Introduction */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-4xl">
              <p className="text-slate-700 text-lg lg:text-xl leading-relaxed mb-6">
                DISC Lens Africa™ is RESILIGENT's proprietary behavioural diagnostic that goes beyond surface-level style identification to reveal how people act, what they value, and what truly motivates them, interpreted through African organisational realities.
              </p>
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                Delivered Through Impact AS Plus™, DISC Lens Africa™ integrates behavioural insight, values-based interpretation, and applied development logic to support leadership effectiveness, team performance, and organisational maturity.
              </p>
            </div>
          </div>
        </section>

        {/* Section 2: Why DISC Lens Africa Exists */}
        <section className="bg-[#f0f5f8] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                  Why DISC Lens Africa™
                </h2>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                  Most DISC-based tools were designed for Western corporate environments and focus primarily on observable behaviour.
                </p>
                <p className="text-slate-700 font-medium text-lg lg:text-xl leading-relaxed">
                  In African organisational contexts, behaviour is inseparable from values, power dynamics, social expectations, and adaptation to context.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-8 card-pulse-border">
                <p className="text-slate-700 text-base lg:text-lg leading-relaxed mb-6">
                  DISC Lens Africa™ was designed to reflect this reality, enabling insight that is:
                </p>
                <ul className="space-y-4">
                  <li className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-lg bg-[#2693d0]/10 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-[#2693d0]" />
                    </div>
                    <span className="text-slate-700 text-base lg:text-lg leading-relaxed">Culturally grounded</span>
                  </li>
                  <li className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-lg bg-[#2693d0]/10 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-[#2693d0]" />
                    </div>
                    <span className="text-slate-700 text-base lg:text-lg leading-relaxed">Developmentally meaningful</span>
                  </li>
                  <li className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-lg bg-[#2693d0]/10 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-[#2693d0]" />
                    </div>
                    <span className="text-slate-700 text-base lg:text-lg leading-relaxed">Organisationally actionable</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Section 3: The Three Lenses */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl mb-16">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                The Three Lenses of DISC Lens Africa™
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {threeLenses.map((lens, index) => {
                const Icon = lens.icon;
                return (
                  <div key={index} className="bg-[#f0f5f8] rounded-xl p-8 border border-slate-100">
                    <div className="w-14 h-14 rounded-xl bg-[#2693d0]/10 flex items-center justify-center mb-6">
                      <Icon className="w-7 h-7 text-[#2693d0]" />
                    </div>
                    <h3 className="text-slate-800 font-semibold text-xl mb-2">{lens.title}</h3>
                    <p className="text-[#2693d0] font-medium text-sm mb-4">{lens.subtitle}</p>
                    <p className="text-slate-600 text-base leading-relaxed">{lens.description}</p>
                  </div>
                );
              })}
            </div>
            
            <div className="bg-[#2693d0]/5 rounded-xl p-6 lg:p-8 border border-[#2693d0]/20 max-w-3xl">
              <p className="text-slate-800 font-medium text-lg lg:text-xl leading-relaxed">
                These three lenses together explain not just what someone does, but why they do it and whether their current way of working is sustainable.
              </p>
            </div>
          </div>
        </section>

        {/* Section 4: Integrated Styles & Authenticity */}
        <section className="bg-[#1e3a5f] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <h2 className="text-white font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                  Integrated Styles and Authenticity
                </h2>
                <p className="text-slate-200 text-base lg:text-lg leading-relaxed mb-6">
                  DISC Lens Africa™ recognises that leaders rarely operate from a single fixed style.
                </p>
                <p className="text-slate-300 text-base lg:text-lg leading-relaxed">
                  Reports reflect integrated behavioural styles (for example: D-I, D-C, S-C, I-S), acknowledging behavioural flexibility across contexts and roles.
                </p>
              </div>
              
              <div>
                <p className="text-slate-200 font-medium text-lg mb-6">Each report highlights:</p>
                <ul className="space-y-4 mb-8">
                  {integratedStyleHighlights.map((item, index) => (
                    <li key={index} className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle className="w-4 h-4 text-[#2693d0]" />
                      </div>
                      <span className="text-white text-base lg:text-lg leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-white/10 rounded-xl p-6 border border-white/20">
                  <p className="text-slate-200 text-base lg:text-lg leading-relaxed">
                    This allows organisations to distinguish <span className="text-white font-medium">healthy adaptability</span> from <span className="text-white font-medium">unsustainable over-extension</span>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 5: Values-Based Leadership Insight */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                  Values-Based Leadership Insight
                </h2>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                  By embedding values directly into behavioural interpretation, DISC Lens Africa™ enables deeper leadership insight.
                </p>
                <p className="text-slate-700 font-medium text-lg lg:text-xl leading-relaxed">
                  This is particularly critical in African organisations, where leadership effectiveness is shaped as much by how decisions are made as by outcomes.
                </p>
              </div>
              
              <div className="bg-[#f0f5f8] rounded-xl p-8 border border-slate-100">
                <p className="text-slate-700 font-medium text-lg mb-6">Organisations gain clarity on:</p>
                <ul className="space-y-4">
                  {valuesInsight.map((item, index) => (
                    <li key={index} className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-lg bg-[#2693d0]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle className="w-4 h-4 text-[#2693d0]" />
                      </div>
                      <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Section 6: From Insight to Development */}
        <section className="bg-[#f0f5f8] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl mb-12">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                From Insight to Development
              </h2>
              <p className="text-slate-700 font-medium text-lg lg:text-xl leading-relaxed mb-6">
                DISC Lens Africa™ is designed to drive behaviour change, not just awareness.
              </p>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8 card-pulse-border">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-[#2693d0]/10 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-[#2693d0]" />
                  </div>
                  <h3 className="text-slate-800 font-semibold text-lg">Each report includes:</h3>
                </div>
                <ul className="space-y-3">
                  {reportIncludes.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                      <span className="text-slate-700 text-base leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-[#2693d0]/5 rounded-xl p-8 border border-[#2693d0]/20 flex flex-col justify-center">
                <p className="text-slate-800 font-medium text-lg lg:text-xl leading-relaxed">
                  This makes DISC Lens Africa™ highly effective within leadership development programmes, coaching engagements, and transformation initiatives.
                </p>
              </div>
            </div>
            
            {/* Explore DISC Lens Africa Button */}
            <div className="mt-12 pt-8 border-t border-slate-200">
              <a
                href="/product/disc-africa"
                className="inline-flex items-center justify-center gap-2 bg-[#2693d0] text-white px-5 py-3 rounded font-medium text-sm hover:bg-[#1e7ab8] transition-all duration-300"
              >
                Explore DISC Lens Africa™
                <ArrowRight size={16} />
              </a>
              <p className="text-slate-500 text-sm mt-4">
                DISC Lens Africa™ is part of the Impact AS Plus™ leadership and talent intelligence system.
              </p>
            </div>
          </div>
        </section>

        {/* Section 7: DISC Lens Africa Teams */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl leading-tight">
                    DISC Lens Teams™
                  </h2>
                  <img 
                    src={discAfricaTeamsLogo} 
                    alt="DISC Lens Teams" 
                    className="h-12 lg:h-14 w-auto hidden sm:block"
                  />
                </div>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                  DISC Lens Teams™ extends the same three-lens framework to teams and leadership groups.
                </p>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                  Common applications include executive committees, leadership teams, cross-functional groups, and project teams.
                </p>
              </div>
              
              <div className="bg-[#f0f5f8] rounded-xl p-8 border border-slate-100">
                <p className="text-slate-700 font-medium text-lg mb-6">It enables insight into:</p>
                <ul className="space-y-4">
                  {teamsInsight.map((item, index) => (
                    <li key={index} className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-lg bg-[#2693d0]/10 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-[#2693d0]" />
                      </div>
                      <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Section 8: Organisational Insight at Scale */}
        <section className="bg-[#f0f5f8] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-[#2693d0]/10 flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-[#2693d0]" />
                  </div>
                  <span className="text-[#2693d0] text-sm font-semibold tracking-wide uppercase">Enterprise Scale</span>
                </div>
                <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                  Organisational Insight at Scale
                </h2>
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                  When deployed across populations, DISC Lens Africa™ enables organisations to see patterns that shape culture, leadership effectiveness, and execution.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-8 card-pulse-border">
                <p className="text-slate-700 font-medium text-lg mb-6">Organisations can identify:</p>
                <ul className="space-y-4 mb-6">
                  {organisationalInsight.map((item, index) => (
                    <li key={index} className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-lg bg-[#2693d0]/10 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-[#2693d0]" />
                      </div>
                      <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-[#f0f5f8] rounded-lg p-4 border border-slate-100">
                  <p className="text-slate-600 text-sm leading-relaxed">
                    This allows organisations to link individual insight to organisational maturity and performance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 9: Comparison with Generic DISC */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                DISC Lens Africa™ and other DISC tools
              </h2>
              
              <p className="text-slate-700 font-medium text-lg lg:text-xl leading-relaxed mb-6">
                DISC Lens Africa™ does not replace all DISC tools. It serves a different and deeper purpose.
              </p>
              
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                Generic DISC tools typically focus on behavioural identification and standardised interpretation.
              </p>
              
              <div className="bg-[#f0f5f8] rounded-xl p-8 border border-slate-100 mb-8">
                <p className="text-slate-700 text-base lg:text-lg leading-relaxed mb-4">
                  <span className="font-semibold text-slate-800">DISC Lens Africa™ extends this</span> by:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-700 text-base leading-relaxed">Integrating behaviour, values, and motivation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-700 text-base leading-relaxed">Enabling integrated style analysis</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-700 text-base leading-relaxed">Supporting individual, team, and organisational insight within a single system</span>
                  </li>
                </ul>
              </div>
              
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed">
                Many organisations introduce DISC Lens Africa™ to extend insight where generic tools stop.
              </p>
            </div>
          </div>
        </section>

        {/* Section 10: Delivered via Impact AS Plus */}
        <section className="bg-[#1e3a5f] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <img 
                  src={impactASPlusLogo} 
                  alt="Impact AS Plus" 
                  className="h-12 lg:h-14 w-auto mb-8"
                />
                <h2 className="text-white font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                  Delivered Through Impact AS Plus™
                </h2>
                <p className="text-slate-200 text-base lg:text-lg leading-relaxed">
                  DISC Lens Africa™ and DISC Lens Teams™ are delivered exclusively through Impact AS Plus™, RESILIGENT's proprietary leadership and organisational intelligence platform.
                </p>
              </div>
              
              <div className="bg-white/10 rounded-xl p-8 border border-white/20">
                <p className="text-slate-200 font-medium text-lg mb-6">This enables:</p>
                <ul className="space-y-4">
                  <li className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-[#2693d0]" />
                    </div>
                    <span className="text-white text-base lg:text-lg leading-relaxed">Secure digital delivery</span>
                  </li>
                  <li className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-[#2693d0]" />
                    </div>
                    <span className="text-white text-base lg:text-lg leading-relaxed">Aggregation of insight</span>
                  </li>
                  <li className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-[#2693d0]" />
                    </div>
                    <span className="text-white text-base lg:text-lg leading-relaxed">Integration with coaching and development workflows</span>
                  </li>
                  <li className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-[#2693d0]" />
                    </div>
                    <span className="text-white text-base lg:text-lg leading-relaxed">Longitudinal tracking over time</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Section 11: CTA */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                Explore DISC Lens Africa™
              </h2>
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-10">
                See how DISC Lens Africa™ can provide deeper leadership insight for your organisation.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/sample-reports"
                  className="inline-flex items-center justify-center gap-2 bg-[#2693d0] text-white px-8 py-4 rounded-lg font-medium hover:bg-[#1e7ab8] transition-all duration-300"
                >
                  View sample reports
                  <ArrowRight className="w-5 h-5" />
                </a>
                <a
                  href="/briefing"
                  className="inline-flex items-center justify-center gap-2 bg-white text-slate-800 px-8 py-4 rounded-lg font-medium border border-slate-200 hover:border-[#2693d0] hover:text-[#2693d0] transition-all duration-300"
                >
                  Book a leadership briefing
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer hidePreFooterCta={true} />
    </div>
  );
};

export default DiscAfricaPage;
